import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  X,
  Plus,
  RefreshCw,
  Award,
  Users,
  Trash2,
  Eye,
  Download,
  Printer,
  Monitor,
  Database,
  Bot,
  Terminal,
  Megaphone,
  Palette,
  Smartphone,
  Briefcase,
  Edit,
  Mail,
  Send,
  Phone,
  BookOpen
} from 'lucide-react';
import Certificate from '../components/Certificate/Certificate';
import { getAdminToken, getBackendUrl } from '../utils/auth';

const API_URL = getBackendUrl();

const AdminCertificates = () => {
  const [activeTab, setActiveTab] = useState('registrations'); // 'registrations' | 'certificates' | 'programs'

  // Data States
  const [certificates, setCertificates] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [programs, setPrograms] = useState([]);

  const [loading, setLoading] = useState(true);

  // Form State for Certificate Generation
  const [selectedRegId, setSelectedRegId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form State for Internship Programs
  const [programForm, setProgramForm] = useState({ title: '', duration: '', modules: '', iconName: 'Briefcase' });
  const [editingProgramId, setEditingProgramId] = useState(null);

  // Preview State
  const [previewCert, setPreviewCert] = useState(null);

  // Manual Registration State
  const [regModal, setRegModal] = useState(false);
  const [manualRegForm, setManualRegForm] = useState({
    name: '', email: '', phone: '', college: '', domain: '', objective: 'Manual Registration (Admin)', startDate: '', endDate: ''
  });
  const [manualRegLoading, setManualRegLoading] = useState(false);

  // Edit Registration State
  const [editingRegistrationId, setEditingRegistrationId] = useState(null);
  const [editingRegistrationForm, setEditingRegistrationForm] = useState({
    name: '', email: '', phone: '', college: '', domain: '', startDate: '', endDate: ''
  });

  // Edit Certificate State
  const [editingCertificateId, setEditingCertificateId] = useState(null);
  const [editingCertificateForm, setEditingCertificateForm] = useState({
    studentName: '', courseName: '', startDate: '', endDate: ''
  });
  const [editCertLoading, setEditCertLoading] = useState(false);

  // Manual Certificate State
  const [manualCertModal, setManualCertModal] = useState(false);
  const [manualCertForm, setManualCertForm] = useState({
    studentName: '', courseName: '', startDate: '', endDate: ''
  });
  const [manualCertLoading, setManualCertLoading] = useState(false);
  const [autoCertModal, setAutoCertModal] = useState(false);
  const [programModal, setProgramModal] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  // Email Modal State
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    certificateId: '',
    recipientEmail: '',
    subject: '',
    body: '',
    studentName: '',
    certificateNo: ''
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Delete OTP State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteCertId, setDeleteCertId] = useState(null);
  const [deleteOtp, setDeleteOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [certRes, regRes, progRes] = await Promise.all([
        fetch(`${API_URL}/api/certificates/all`),
        fetch(`${API_URL}/api/internship/all`, {
          headers: { 'Authorization': `Bearer ${getAdminToken()}` }
        }),
        fetch(`${API_URL}/api/internship/programs`)
      ]);

      const certData = await certRes.json();
      const regData = await regRes.json();
      const progData = await progRes.json();

      if (certData.success) setCertificates(certData.certificates);
      if (regData.success) setRegistrations(regData.registrations);
      if (progData.success) setPrograms(progData.programs);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- REGISTRATION LOGIC ---
  const handleRegStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/internship/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.emailStatus === 'sent') {
        setMessage({ type: 'success', text: 'Status updated and acceptance email sent successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      } else if (data.emailStatus === 'failed') {
        setMessage({ type: 'error', text: 'Status updated, but failed to send email.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
      await fetchData(true);
    } catch (error) {
      console.error('Error updating registration:', error);
    }
  };

  const handleDeleteReg = async (id) => {
    if (!window.confirm("Are you sure you want to delete this registration?")) return;
    try {
      await fetch(`${API_URL}/api/internship/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAdminToken()}`
        }
      });
      await fetchData(true);
    } catch (error) {
      console.error('Error deleting registration:', error);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setManualRegLoading(true);
    try {
      const url = editingRegistrationId ? `${API_URL}/api/internship/${editingRegistrationId}` : `${API_URL}/api/internship/apply`;
      const method = editingRegistrationId ? "PUT" : "POST";

      const payload = editingRegistrationId ? editingRegistrationForm : manualRegForm;
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error("Failed to process registration");

      setManualRegForm({ name: '', email: '', phone: '', college: '', domain: '', objective: 'Manual Registration (Admin)', startDate: '', endDate: '' });
      setEditingRegistrationId(null);
      setRegModal(false);
      await fetchData(true);
    } catch (error) {
      console.error('Error processing registration:', error);
      alert("Error processing registration");
    } finally {
      setManualRegLoading(false);
    }
  };

  // --- CERTIFICATE LOGIC ---
  const handleGenerateCertificate = async (e) => {
    e.preventDefault();
    if (!selectedRegId) return;

    const student = registrations.find(r => r._id === selectedRegId);
    if (!student) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/certificates/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({
          studentName: student.name,
          courseName: student.domain,
          startDate: student.startDate,
          endDate: student.endDate
        })
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `Certificate generated: ${data.certificate.certificateNo}` });
        setSelectedRegId('');
        setAutoCertModal(false);
        await fetchData(true);
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error generating certificate.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualCertSubmit = async (e) => {
    e.preventDefault();
    setManualCertLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/certificates/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify(manualCertForm)
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `Certificate generated: ${data.certificate.certificateNo}` });
        setManualCertModal(false);
        setManualCertForm({ studentName: '', courseName: '', startDate: '', endDate: '' });
        await fetchData(true);
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error generating certificate.' });
    } finally {
      setManualCertLoading(false);
    }
  };

  const handleGenerateDirect = async (reg) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/certificates/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({
          studentName: reg.name,
          courseName: reg.domain,
          startDate: reg.startDate,
          endDate: reg.endDate
        })
      });
      const data = await response.json();

      if (data.success) {
        alert(`Certificate generated: ${data.certificate.certificateNo}`);
        await fetchData(true);
        // Automatically prompt to email it!
        if (window.confirm('Certificate generated! Do you want to email it to the student now?')) {
          handleOpenEmailModal(data.certificate);
        }
      } else {
        alert(data.message || 'Failed to generate certificate');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Error generating certificate.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEmailModal = (cert) => {
    // Try to find the student's email from registrations
    const registration = registrations.find(r => r.name.toLowerCase() === cert.studentName.toLowerCase());
    const studentEmail = registration ? registration.email : '';
    const appName = 'CLIENTCODEAI';

    setEmailModal({
      isOpen: true,
      certificateId: cert._id,
      recipientEmail: studentEmail,
      subject: `Certificate of Completion - ${cert.studentName}`,
      body: `Hi ${cert.studentName},\n\nCongratulations on successfully completing your internship!\n\nPlease find attached your Certificate of Completion (${cert.certificateNo}) for the ${cert.courseName} program.\n\nBest regards,\nTeam ${appName}`,
      studentName: cert.studentName,
      certificateNo: cert.certificateNo
    });
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/certificates/${emailModal.certificateId}/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({
          email: emailModal.recipientEmail,
          subject: emailModal.subject,
          body: emailModal.body.replace(/\n/g, '<br/>') // convert newlines to HTML br
        })
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message || 'Email sent successfully!');
        setEmailModal(prev => ({ ...prev, isOpen: false }));
      } else {
        alert(data.message || 'Failed to send email.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleCertStatus = async (id, newStatus) => {
    try {
      await fetch(`${API_URL}/api/certificates/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      await fetchData(true);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleRequestDeleteOtp = async (id) => {
    setIsSendingOtp(true);
    try {
      const response = await fetch(`${API_URL}/api/certificates/delete-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAdminToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setDeleteCertId(id);
        setDeleteModalOpen(true);
        setDeleteOtp('');
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error requesting delete OTP:', error);
      alert('Error requesting OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const confirmDeleteCert = async (e) => {
    e.preventDefault();
    if (!deleteOtp) return alert('Please enter OTP');

    setIsVerifyingOtp(true);
    try {
      const response = await fetch(`${API_URL}/api/certificates/${deleteCertId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({ otp: deleteOtp })
      });
      const data = await response.json();
      if (data.success) {
        await fetchData(true);
        setMessage({ type: 'success', text: 'Certificate deleted successfully.' });
        setDeleteModalOpen(false);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      alert('Error deleting certificate');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleEditCertificateSubmit = async (e) => {
    e.preventDefault();
    setEditCertLoading(true);
    try {
      const payload = { ...editingCertificateForm };
      if (!payload.startDate) payload.startDate = null;
      if (!payload.endDate) payload.endDate = null;

      const response = await fetch(`${API_URL}/api/certificates/${editingCertificateId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update certificate");
      }

      setEditingCertificateId(null);
      await fetchData(true);
    } catch (error) {
      console.error('Error updating certificate:', error);
      alert(error.message || "Error updating certificate");
    } finally {
      setEditCertLoading(false);
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    if (!programForm.title || !programForm.duration) {
      return setMessage({ type: 'error', text: 'Title and duration are required.' });
    }

    const parsedModules = programForm.modules
      .split(/[,\n]/)
      .map(m => m.trim())
      .filter(Boolean);

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const url = editingProgramId
        ? `${API_URL}/api/internship/programs/${editingProgramId}`
        : `${API_URL}/api/internship/programs`;
      const method = editingProgramId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({
          title: programForm.title,
          duration: programForm.duration,
          modules: parsedModules,
          iconName: programForm.iconName
        })
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: editingProgramId ? 'Program updated successfully!' : 'Program added successfully!' });
        setProgramForm({ title: '', duration: '', modules: '', iconName: 'Briefcase' });
        setEditingProgramId(null);
        setProgramModal(false);
        await fetchData(true);
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Error saving program details:', error);
      setMessage({ type: 'error', text: `Error saving program: ${error.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProgram = (program) => {
    setProgramForm({
      title: program.title,
      duration: program.duration,
      modules: program.modules.join(', '),
      iconName: program.iconName || 'Briefcase'
    });
    setEditingProgramId(program._id);
    setProgramModal(true);
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm("Are you sure you want to delete this internship program?")) return;
    try {
      const response = await fetch(`${API_URL}/api/internship/programs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAdminToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        await fetchData(true);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  const renderProgramIcon = (iconName) => {
    const iconProps = { className: "w-6 h-6 text-[#7C4DFF]" };
    switch (iconName) {
      case 'Monitor': return <Monitor {...iconProps} />;
      case 'Database': return <Database {...iconProps} />;
      case 'Bot': return <Bot {...iconProps} />;
      case 'Terminal': return <Terminal {...iconProps} />;
      case 'Megaphone': return <Megaphone {...iconProps} />;
      case 'Palette': return <Palette {...iconProps} />;
      case 'Smartphone': return <Smartphone {...iconProps} />;
      default: return <Briefcase {...iconProps} />;
    }
  };

  const approvedStudents = registrations.filter(r => r.status === 'Approved');

  return (
    <div className="w-full max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#F3EEFF] rounded-xl flex items-center justify-center border border-[#7C4DFF]/20">
            <Award className="w-6 h-6 text-[#7C4DFF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#111827] ">Internship Portal</h1>
            <p className="text-[#6B7280] text-sm">Manage applications and generate certificates.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-[#F3F4F6] rounded-xl p-1 border border-[#E5E7EB]">
          <button
            onClick={() => setActiveTab('registrations')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all text-sm ${activeTab === 'registrations' ? 'bg-white text-[#7C4DFF] shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'}`}
          >
            <Users className="w-4 h-4" /> Applications
          </button>
          <button
            onClick={() => setActiveTab('certificates')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all text-sm ${activeTab === 'certificates' ? 'bg-white text-[#7C4DFF] shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'}`}
          >
            <Award className="w-4 h-4" /> Certificates
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all text-sm ${activeTab === 'programs' ? 'bg-white text-[#7C4DFF] shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'}`}
          >
            <Briefcase className="w-4 h-4" /> Programs
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#7C4DFF] flex flex-col items-center bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <RefreshCw className="w-8 h-8 animate-spin mb-4 text-[#D1D5DB]" />
          <p className="text-[#6B7280] font-medium">Loading Database...</p>
        </div>
      ) : activeTab === 'registrations' ? (
        /* REGISTRATIONS TAB */
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] overflow-hidden"
        >
          <div className="flex justify-between items-center p-6 border-b border-[#E5E7EB]">
            <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2">
              Student Applications
              {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-[#7C4DFF]" />}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingRegistrationId(null);
                  setManualRegForm({ name: '', email: '', phone: '', college: '', domain: '', objective: 'Manual Registration (Admin)', startDate: '', endDate: '' });
                  setRegModal(true);
                }}
                className="flex items-center gap-2 bg-[#7C4DFF] hover:bg-[#6B44DE] text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
              >
                <Plus className="w-4 h-4" /> Add Manual
              </button>
              <button onClick={() => fetchData(true)} className="text-[#6B7280] hover:text-[#7C4DFF] bg-[#F9FAFB] p-2 rounded-lg border border-[#E5E7EB] transition-colors">
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin text-[#7C4DFF]' : ''}`} />
              </button>
            </div>
          </div>



          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[#4B5563] text-sm font-semibold uppercase tracking-wider">
                  <th className="py-4 px-6 min-w-[200px]">Applicant</th>
                  <th className="py-4 px-6 min-w-[200px]">Contact & Edu</th>
                  <th className="py-4 px-6 min-w-[180px]">Program Info</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {registrations.map(reg => (
                  <tr key={reg._id} className="border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="font-bold text-[#111827]">{reg.name}</div>
                      <div className="text-xs text-[#6B7280] mt-1 line-clamp-2" title={reg.objective}>{reg.objective}</div>
                    </td>
                    <td className="py-4 px-6 text-[#6B7280]">
                      <div className="flex items-center gap-2 text-xs mb-1"><Mail className="w-3 h-3" /> {reg.email}</div>
                      <div className="flex items-center gap-2 text-xs mb-1"><Phone className="w-3 h-3" /> {reg.phone}</div>
                      <div className="flex items-center gap-2 text-xs"><BookOpen className="w-3 h-3" /> {reg.college}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-medium text-[#7C4DFF] mb-1">{reg.domain}</div>
                      <div className="text-xs text-[#6B7280]">
                        {reg.startDate ? new Date(reg.startDate).toLocaleDateString('en-GB') : 'TBA'} - {reg.endDate ? new Date(reg.endDate).toLocaleDateString('en-GB') : 'TBA'}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${reg.status === 'Approved' ? 'bg-[#D1FAE5] text-[#059669]' :
                          reg.status === 'Rejected' ? 'bg-[#FEE2E2] text-[#DC2626]' :
                            'bg-[#FEF3C7] text-[#D97706]'
                        }`}>{reg.status}</span>
                    </td>
                    <td className="py-4 px-6 flex justify-end gap-2">
                      {reg.status === 'Approved' && !certificates.some(c => c.studentName.toLowerCase() === reg.name.toLowerCase() && c.courseName.toLowerCase() === reg.domain.toLowerCase()) && (
                        <button onClick={() => handleGenerateDirect(reg)} className="p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200" title="Generate Certificate"><Award className="w-4 h-4" /></button>
                      )}
                      {reg.status !== 'Approved' && (
                        <button onClick={() => handleRegStatus(reg._id, 'Approved')} className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 border border-green-200" title="Approve"><CheckCircle className="w-4 h-4" /></button>
                      )}
                      {reg.status !== 'Rejected' && (
                        <button onClick={() => handleRegStatus(reg._id, 'Rejected')} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-200" title="Reject"><XCircle className="w-4 h-4" /></button>
                      )}
                      <button
                        onClick={() => {
                          setEditingRegistrationId(reg._id);
                          setEditingRegistrationForm({
                            name: reg.name, email: reg.email, phone: reg.phone, college: reg.college, domain: reg.domain, startDate: reg.startDate, endDate: reg.endDate
                          });
                          setRegModal(true);
                        }}
                        className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200" title="Edit"
                      ><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteReg(reg._id)} className="p-2 rounded-lg bg-white border border-[#FCA5A5] text-[#EF4444] hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {registrations.length === 0 && <div className="text-center py-16 text-[#6B7280] font-medium bg-[#F9FAFB]">No applications found.</div>}
          </div>
        </motion.div>
      ) : activeTab === 'certificates' ? (
        /* CERTIFICATES TAB */
        <div className="w-full">
          {message.text && (
            <div className={`p-4 rounded-xl mb-6 text-sm font-medium border ${message.type === 'success' ? 'bg-[#D1FAE5] text-[#059669] border-[#10B981]/30' : 'bg-[#FEE2E2] text-[#DC2626] border-[#EF4444]/30'}`}>
              {message.text}
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-[#E5E7EB]">
              <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2">
                Generated Certificates
                {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-[#7C4DFF]" />}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setAutoCertModal(true)}
                  className="bg-white border border-[#D1D5DB] text-[#111827] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <Award className="w-4 h-4 text-[#7C4DFF]" /> Auto-Generate
                </button>
                <button
                  onClick={() => setManualCertModal(true)}
                  className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Issue Manual Cert
                </button>
                <button onClick={() => fetchData(true)} className="text-[#6B7280] hover:text-[#7C4DFF] bg-[#F9FAFB] p-2 rounded-lg border border-[#E5E7EB] transition-colors">
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin text-[#7C4DFF]' : ''}`} />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {editingCertificateId && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-[#E5E7EB] bg-[#F9FAFB] overflow-hidden"
                >
                  <div className="p-6 border-b border-[#E5E7EB] flex justify-between items-center bg-white">
                    <h3 className="font-bold text-[#111827]">Edit Certificate Details</h3>
                    <button onClick={() => setEditingCertificateId(null)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleEditCertificateSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input required placeholder="Student Name" value={editingCertificateForm.studentName} onChange={e => setEditingCertificateForm({ ...editingCertificateForm, studentName: e.target.value })} className="px-4 py-2 rounded-lg border border-[#D1D5DB] outline-none focus:border-[#7C4DFF]" />
                    <input required placeholder="Course Name" value={editingCertificateForm.courseName} onChange={e => setEditingCertificateForm({ ...editingCertificateForm, courseName: e.target.value })} className="px-4 py-2 rounded-lg border border-[#D1D5DB] outline-none focus:border-[#7C4DFF]" />
                    <input type="date" title="Start Date" required value={editingCertificateForm.startDate ? new Date(editingCertificateForm.startDate).toISOString().split('T')[0] : ''} onChange={e => setEditingCertificateForm({ ...editingCertificateForm, startDate: e.target.value })} className="px-4 py-2 rounded-lg border border-[#D1D5DB] outline-none focus:border-[#7C4DFF]" />
                    <input type="date" title="End Date" required value={editingCertificateForm.endDate ? new Date(editingCertificateForm.endDate).toISOString().split('T')[0] : ''} onChange={e => setEditingCertificateForm({ ...editingCertificateForm, endDate: e.target.value })} className="px-4 py-2 rounded-lg border border-[#D1D5DB] outline-none focus:border-[#7C4DFF]" />
                    <button type="submit" disabled={editCertLoading} className="bg-[#7C4DFF] hover:bg-[#6B44DE] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 md:col-span-2">
                      {editCertLoading ? 'Saving...' : 'Update Certificate'}
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[#4B5563] text-sm font-semibold uppercase tracking-wider">
                    <th className="py-4 px-6">Cert ID</th>
                    <th className="py-4 px-6">Student</th>
                    <th className="py-4 px-6">Course</th>
                    <th className="py-4 px-6">Status</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {certificates.map(cert => (
                    <tr key={cert._id} className="border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-[#7C4DFF]">{cert.certificateNo}</td>
                      <td className="py-4 px-6 font-bold text-[#111827]">{cert.studentName}</td>
                      <td className="py-4 px-6 font-medium text-[#4B5563]">{cert.courseName}</td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${cert.status === 'Accepted' ? 'bg-[#D1FAE5] text-[#059669]' :
                            cert.status === 'Rejected' ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#FEF3C7] text-[#D97706]'
                          }`}>{cert.status}</span>
                      </td>
                      <td className="py-4 px-6 flex justify-end gap-2">
                        <button onClick={() => handleOpenEmailModal(cert)} className="p-2 rounded-lg bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border border-yellow-200" title="Send Email"><Mail className="w-4 h-4" /></button>
                        <button onClick={() => setPreviewCert(cert)} className="p-2 rounded-lg bg-[#F3EEFF] text-[#7C4DFF] hover:bg-[#E9D5FF] border border-[#7C4DFF]/20" title="View"><Eye className="w-4 h-4" /></button>
                        <button
                          onClick={() => {
                            setEditingCertificateId(cert._id);
                            setEditingCertificateForm({
                              studentName: cert.studentName, courseName: cert.courseName, startDate: cert.startDate, endDate: cert.endDate
                            });
                          }}
                          className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200" title="Edit"
                        ><Edit className="w-4 h-4" /></button>
                        {cert.status !== 'Accepted' && <button onClick={() => handleCertStatus(cert._id, 'Accepted')} className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 border border-green-200"><CheckCircle className="w-4 h-4" /></button>}
                        {cert.status !== 'Rejected' && <button onClick={() => handleCertStatus(cert._id, 'Rejected')} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"><XCircle className="w-4 h-4" /></button>}
                        <button onClick={() => handleRequestDeleteOtp(cert._id)} disabled={isSendingOtp} className="p-2 rounded-lg bg-white border border-[#FCA5A5] text-[#EF4444] hover:bg-red-50 disabled:opacity-50" title="Delete">{isSendingOtp && deleteCertId === cert._id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {certificates.length === 0 && <div className="text-center py-16 text-[#6B7280] font-medium bg-[#F9FAFB]">No certificates generated yet.</div>}
            </div>
          </motion.div>
        </div>
      ) : activeTab === 'programs' ? (
        /* PROGRAMS TAB */
        <div className="w-full space-y-6">
          {message.text && (
            <div className={`p-4 rounded-xl mb-6 text-sm font-medium border ${message.type === 'success' ? 'bg-[#D1FAE5] text-[#059669] border-[#10B981]/30' : 'bg-[#FEE2E2] text-[#DC2626] border-[#EF4444]/30'}`}>
              {message.text}
            </div>
          )}
          {/* Programs List */}
          <motion.div
            initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
            className="w-full space-y-6"
          >
            <div className="bg-white p-6 rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2">
                  Active Internship Programs
                  {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-[#7C4DFF]" />}
                </h2>
                <p className="text-sm text-[#6B7280] mt-0.5">Total programs: {programs.length}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setEditingProgramId(null);
                    setProgramForm({ title: '', duration: '', modules: '', iconName: 'Briefcase' });
                    setProgramModal(true);
                  }}
                  className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Program
                </button>
                <button onClick={() => fetchData(true)} className="text-[#6B7280] hover:text-[#7C4DFF] bg-[#F9FAFB] p-2 rounded-lg border border-[#E5E7EB] transition-colors">
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin text-[#7C4DFF]' : ''}`} />
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {programs.length === 0 ? (
                <div className="sm:col-span-2 bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center text-[#6B7280]">
                  No programs added yet. Add your first internship program on the left.
                </div>
              ) : (
                programs.map(prog => (
                  <div key={prog._id} className="bg-white p-6 rounded-2xl border border-[#E5E7EB] shadow-[0_4px_12px_rgba(0,0,0,0.03)] flex flex-col justify-between relative group">
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#F3EEFF] flex items-center justify-center border border-[#7C4DFF]/15">
                          {renderProgramIcon(prog.iconName)}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditProgram(prog)}
                            className="p-2 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 transition-colors"
                            title="Edit Program"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProgram(prog._id)}
                            className="p-2 rounded-lg bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors"
                            title="Delete Program"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-[#111827]">{prog.title}</h3>
                      <p className="text-sm font-semibold text-[#7C4DFF] mt-1 mb-4">{prog.duration}</p>

                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider">Modules:</p>
                        <ul className="text-sm text-[#6B7280] list-disc list-inside space-y-1">
                          {prog.modules.slice(0, 5).map((mod, i) => (
                            <li key={i} className="truncate">{mod}</li>
                          ))}
                          {prog.modules.length > 5 && (
                            <li className="text-[#7C4DFF] text-xs font-semibold list-none mt-1">+{prog.modules.length - 5} more modules</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      ) : null}

      {/* Preview Modal */}
      {createPortal(
        <AnimatePresence>
          {previewCert && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative max-w-5xl w-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
              >
                {/* Modal Header with Actions (No overlap) */}
                <div className="w-full flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] text-lg">Certificate Preview</h3>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={async () => {
                        if (isDownloading) return;
                        setIsDownloading(true);
                        try {
                          let downloadUrl;
                          if (previewCert.certificateNo) {
                            downloadUrl = `${API_URL}/api/certificates/download/${previewCert.certificateNo}`;
                          } else {
                            // Fallback to passing data directly for unsaved previews
                            const queryData = encodeURIComponent(JSON.stringify(previewCert));
                            downloadUrl = `${API_URL}/api/certificates/download/preview?data=${queryData}`;
                          }

                          const response = await fetch(downloadUrl);
                          if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(errorText || "Failed to generate PDF from server");
                          }

                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${previewCert.studentName.replace(/\s+/g, '_')}_Certificate.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error("PDF Generation Error:", error);
                          alert(`Failed to generate PDF. Error details: ${error.message || error}`);
                        } finally {
                          setIsDownloading(false);
                        }
                      }}
                      disabled={isDownloading}
                      className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-md text-white px-5 py-2 rounded-lg transition-all flex items-center gap-2"
                      title="Download PDF"
                    >
                      {isDownloading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Download className="w-5 h-5" />}
                      <span className="text-sm font-bold">Download PDF</span>
                    </button>
                    <button
                      onClick={() => {
                        const element = document.getElementById('certificate-download-area');
                        if (!element) return;

                        // Extract all stylesheets from the main document to preserve Tailwind classes
                        const styleTags = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
                          .map(tag => tag.outerHTML)
                          .join('\n');

                        // Create a hidden iframe
                        const iframe = document.createElement('iframe');
                        iframe.style.position = 'fixed';
                        iframe.style.right = '100%';
                        iframe.style.bottom = '100%';
                        iframe.style.width = '1123px';
                        iframe.style.height = '794px';
                        iframe.style.border = 'none';
                        document.body.appendChild(iframe);

                        const iframeDoc = iframe.contentWindow.document;
                        iframeDoc.open();
                        iframeDoc.write(`
                            <html>
                              <head>
                                <title>Print Certificate</title>
                                <base href="${window.location.origin}" />
                                ${styleTags}
                                <style>
                                  @page { size: A4 landscape; margin: 0; }
                                  body { margin: 0; padding: 0; background: white; display: block; }
                                  #certificate-download-area { 
                                    box-shadow: none !important; 
                                    margin: 0 !important; 
                                    width: 297mm !important;
                                    height: 210mm !important;
                                    transform: none !important;
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                  }
                                </style>
                              </head>
                              <body>
                                ${element.outerHTML}
                              </body>
                            </html>
                          `);
                        iframeDoc.close();

                        // Wait for styles/images to load in iframe before printing
                        setTimeout(() => {
                          iframe.contentWindow.focus();
                          iframe.contentWindow.print();
                          setTimeout(() => {
                            document.body.removeChild(iframe);
                          }, 1000);
                        }, 500);
                      }}
                      className="bg-[#1E3A8A] hover:bg-[#111827] hover:shadow-md text-white px-5 py-2 rounded-lg transition-all flex items-center gap-2"
                      title="Print Certificate"
                    >
                      <Printer className="w-5 h-5" />
                      <span className="text-sm font-bold">Print</span>
                    </button>
                    <button
                      onClick={() => setPreviewCert(null)}
                      className="text-[#6B7280] hover:text-[#111827] bg-white hover:bg-gray-50 p-2 rounded-full transition-colors border border-[#D1D5DB]"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Certificate Preview Area */}
                <div className="w-full flex justify-center items-center bg-[#F3F4F6] p-8 min-h-[60vh]">
                  <div
                    className="relative overflow-hidden shadow-2xl rounded"
                    style={{ width: '673.8px', height: '476.4px' }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transform: 'scale(0.6)',
                      transformOrigin: 'top left'
                    }}>
                      <Certificate
                        studentName={previewCert.studentName}
                        courseName={previewCert.courseName}
                        certificateId={previewCert.certificateNo}
                        issueDate={previewCert.createdAt}
                        startDate={previewCert.startDate}
                        endDate={previewCert.endDate}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Email Modal */}
      {createPortal(
        <AnimatePresence>
          {emailModal.isOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E5E7EB]"
              >
                {/* Modal Header */}
                <div className="w-full flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#7C4DFF]" /> Send Certificate to Email
                  </h3>
                  <button
                    onClick={() => setEmailModal(prev => ({ ...prev, isOpen: false }))}
                    className="text-gray-500 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Form */}
                <form onSubmit={handleSendEmail} className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Recipient Name</label>
                    <input
                      type="text"
                      disabled
                      value={emailModal.studentName}
                      className="w-full h-[40px] px-3 rounded-lg bg-gray-100 border border-[#D1D5DB] text-[#4B5563] outline-none font-medium cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Recipient Email</label>
                    <input
                      type="email"
                      required
                      placeholder="student@example.com"
                      value={emailModal.recipientEmail}
                      onChange={e => setEmailModal(prev => ({ ...prev, recipientEmail: e.target.value }))}
                      className="w-full h-[40px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Email Subject</label>
                    <input
                      type="text"
                      required
                      placeholder="Enter email subject"
                      value={emailModal.subject}
                      onChange={e => setEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full h-[40px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Email Content</label>
                    <textarea
                      required
                      rows={6}
                      placeholder="Enter email body content..."
                      value={emailModal.body}
                      onChange={e => setEmailModal(prev => ({ ...prev, body: e.target.value }))}
                      className="w-full p-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow text-sm resize-none"
                    />
                  </div>

                  <div className="flex gap-4 pt-2">
                    <button
                      type="button"
                      onClick={() => setEmailModal(prev => ({ ...prev, isOpen: false }))}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSendingEmail}
                      className="w-1/2 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSendingEmail ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Send Email</>}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Delete OTP Modal */}
      {createPortal(
        <AnimatePresence>
          {deleteModalOpen && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-red-500" /> Verify Deletion
                  </h3>
                  <button onClick={() => setDeleteModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={confirmDeleteCert} className="p-6 space-y-5">
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      An OTP has been sent to the admin email. Please enter it below to confirm the deletion of this certificate.
                    </p>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Enter OTP</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 123456"
                      value={deleteOtp}
                      onChange={e => setDeleteOtp(e.target.value)}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-shadow"
                    />
                  </div>

                  <div className="flex gap-4 pt-2">
                    <button
                      type="button"
                      onClick={() => setDeleteModalOpen(false)}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isVerifyingOtp}
                      className="w-1/2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isVerifyingOtp ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Confirm Delete'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Manual Certificate Modal */}
      {createPortal(
        <AnimatePresence>
          {manualCertModal && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#7C4DFF]" /> Issue Manual Certificate
                  </h3>
                  <button onClick={() => setManualCertModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleManualCertSubmit} className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-[#4B5563] block mb-1">Student Name</label>
                      <input
                        required
                        placeholder="e.g. John Doe"
                        value={manualCertForm.studentName}
                        onChange={e => setManualCertForm({ ...manualCertForm, studentName: e.target.value })}
                        className="w-full h-[42px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-[#4B5563] block mb-1">Course Name</label>
                      <input
                        required
                        placeholder="e.g. Full Stack Web Development"
                        value={manualCertForm.courseName}
                        onChange={e => setManualCertForm({ ...manualCertForm, courseName: e.target.value })}
                        className="w-full h-[42px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#4B5563] block mb-1">Start Date</label>
                      <input
                        type="date"
                        required
                        value={manualCertForm.startDate}
                        onChange={e => setManualCertForm({ ...manualCertForm, startDate: e.target.value })}
                        className="w-full h-[42px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#4B5563] block mb-1">End Date</label>
                      <input
                        type="date"
                        required
                        value={manualCertForm.endDate}
                        onChange={e => setManualCertForm({ ...manualCertForm, endDate: e.target.value })}
                        className="w-full h-[42px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 mt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setManualCertModal(false)}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={manualCertLoading}
                      className="w-1/2 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {manualCertLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Generate Certificate'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
      {/* Auto Generate Certificate Modal */}
      {createPortal(
        <AnimatePresence>
          {autoCertModal && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2">
                    <Plus className="w-5 h-5 text-[#7C4DFF]" /> Auto-Generate Certificate
                  </h3>
                  <button onClick={() => setAutoCertModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleGenerateCertificate} className="p-6 space-y-5">
                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-2">Select Approved Student</label>
                    <select
                      required
                      value={selectedRegId}
                      onChange={(e) => setSelectedRegId(e.target.value)}
                      className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    >
                      <option value="">-- Choose Student --</option>
                      {approvedStudents.map(s => (
                        <option key={s._id} value={s._id}>{s.name} ({s.domain})</option>
                      ))}
                    </select>
                    {approvedStudents.length === 0 && <p className="text-xs text-[#DC2626] mt-2 font-medium">No approved students available. Please approve applications first.</p>}
                  </div>

                  <div className="flex gap-4 pt-4 mt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setAutoCertModal(false)}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit" disabled={isSubmitting || !selectedRegId}
                      className="w-1/2 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Generate'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
      {/* Program Modal */}
      {createPortal(
        <AnimatePresence>
          {programModal && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 z-10 flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#7C4DFF]" />
                    {editingProgramId ? 'Edit Program' : 'Add New Program'}
                  </h3>
                  <button onClick={() => setProgramModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleCreateProgram} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-1">Course Title</label>
                    <input
                      type="text"
                      required
                      value={programForm.title}
                      onChange={e => setProgramForm({ ...programForm, title: e.target.value })}
                      placeholder="e.g. Frontend Development"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-1">Duration</label>
                    <input
                      type="text"
                      required
                      value={programForm.duration}
                      onChange={e => setProgramForm({ ...programForm, duration: e.target.value })}
                      placeholder="e.g. 3 Months"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-1">Select Icon</label>
                    <select
                      value={programForm.iconName}
                      onChange={e => setProgramForm({ ...programForm, iconName: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    >
                      <option value="Monitor">Monitor (Tech/Frontend)</option>
                      <option value="Database">Database (Backend/Data)</option>
                      <option value="Bot">Bot (AI/ML)</option>
                      <option value="Terminal">Terminal (Coding/Python)</option>
                      <option value="Megaphone">Megaphone (Marketing)</option>
                      <option value="Palette">Palette (UI/UX Design)</option>
                      <option value="Smartphone">Smartphone (Mobile App)</option>
                      <option value="Briefcase">Briefcase (General)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-1">Modules (comma or newline separated)</label>
                    <textarea
                      rows="4"
                      required
                      value={programForm.modules}
                      onChange={e => setProgramForm({ ...programForm, modules: e.target.value })}
                      placeholder="HTML5, React.js, Tailwind CSS..."
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>

                  <div className="flex gap-4 pt-4 mt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setProgramModal(false)}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-1/2 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? <RefreshCw className="w-5 h-5 animate-spin" /> : editingProgramId ? 'Update Program' : 'Add Program'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
      {/* Registration Modal */}
      {createPortal(
        <AnimatePresence>
          {regModal && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 z-10 flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#7C4DFF]" />
                    {editingRegistrationId ? 'Edit Application' : 'Add Manual Application'}
                  </h3>
                  <button onClick={() => { setRegModal(false); setEditingRegistrationId(null); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleRegistrationSubmit} className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">Name</label>
                      <input required placeholder="John Doe" value={editingRegistrationId ? editingRegistrationForm.name : manualRegForm.name} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, name: e.target.value }) : setManualRegForm({ ...manualRegForm, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">Email</label>
                      <input required type="email" placeholder="john@example.com" value={editingRegistrationId ? editingRegistrationForm.email : manualRegForm.email} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, email: e.target.value }) : setManualRegForm({ ...manualRegForm, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">Phone</label>
                      <input required placeholder="1234567890" value={editingRegistrationId ? editingRegistrationForm.phone : manualRegForm.phone} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, phone: e.target.value }) : setManualRegForm({ ...manualRegForm, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">College/University</label>
                      <input required placeholder="University Name" value={editingRegistrationId ? editingRegistrationForm.college : manualRegForm.college} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, college: e.target.value }) : setManualRegForm({ ...manualRegForm, college: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">Program / Domain</label>
                      <select required value={editingRegistrationId ? editingRegistrationForm.domain : manualRegForm.domain} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, domain: e.target.value }) : setManualRegForm({ ...manualRegForm, domain: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none bg-white">
                        <option value="">-- Select Domain --</option>
                        {programs.map(p => <option key={p._id} value={p.title}>{p.title}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">Start Date</label>
                      <input type="date" required value={editingRegistrationId ? (editingRegistrationForm.startDate ? new Date(editingRegistrationForm.startDate).toISOString().split('T')[0] : '') : manualRegForm.startDate} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, startDate: e.target.value }) : setManualRegForm({ ...manualRegForm, startDate: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1">End Date</label>
                      <input type="date" required value={editingRegistrationId ? (editingRegistrationForm.endDate ? new Date(editingRegistrationForm.endDate).toISOString().split('T')[0] : '') : manualRegForm.endDate} onChange={e => editingRegistrationId ? setEditingRegistrationForm({ ...editingRegistrationForm, endDate: e.target.value }) : setManualRegForm({ ...manualRegForm, endDate: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none" />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => { setRegModal(false); setEditingRegistrationId(null); }}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={manualRegLoading}
                      className="w-1/2 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {manualRegLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : editingRegistrationId ? 'Update Application' : 'Save Application'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default AdminCertificates;
